// ─── Types ────────────────────────────────────────────────────────────────────

export const IMPORT_TYPE = {
  HANDLING_UNITS: 'handling-units',
  ORDERS: 'orders',
} as const;
export type ImportType = (typeof IMPORT_TYPE)[keyof typeof IMPORT_TYPE];

export interface ImportDocumentationItem {
  key: string;
  values?: Record<string, string>;
}

export interface ImportDocumentationSection {
  introKey: string;
  itemKeys: readonly ImportDocumentationItem[];
  titleKey: string;
}

export interface ImportTypeConfig {
  active: boolean;
  documentationDescriptionKey: string;
  documentationUrl: string;
  labelKey: string;
  noteKey?: string;
  sections?: readonly ImportDocumentationSection[];
  templateEndpoint?: string;
  templateNameKey: string;
  type: ImportType;
  uploadEndpoint: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const ORDERS_CONFIG: ImportTypeConfig = {
  active: true,
  documentationDescriptionKey: 'types.orders.documentationDescription',
  // Placeholder — the PHA-556 help-center article does not exist yet.
  documentationUrl: 'https://docs.klareo.com/imports/orders',
  labelKey: 'importType.orders',
  noteKey: 'types.orders.note',
  sections: [
    {
      introKey: 'types.orders.address.intro',
      itemKeys: [
        { key: 'types.orders.address.fullAddress', values: { field: 'delivery.address.full' } },
        { key: 'types.orders.address.brokenDown' },
      ],
      titleKey: 'types.orders.address.title',
    },
    {
      introKey: 'types.orders.quantities.intro',
      itemKeys: [
        { key: 'types.orders.quantities.quantityOnly' },
        { key: 'types.orders.quantities.weightAndVolume' },
        { key: 'types.orders.quantities.allThree' },
      ],
      titleKey: 'types.orders.quantities.title',
    },
  ],
  templateEndpoint: '/import/templates/orders',
  templateNameKey: 'types.orders.templateName',
  type: IMPORT_TYPE.ORDERS,
  uploadEndpoint: '/import/orders',
};

const HANDLING_UNITS_CONFIG: ImportTypeConfig = {
  active: true,
  documentationDescriptionKey: 'types.handlingUnits.documentationDescription',
  // Placeholder — the handling-units help-center article does not exist yet.
  documentationUrl: 'https://docs.klareo.com/imports/handling-units',
  labelKey: 'importType.handlingUnits',
  sections: [
    {
      introKey: 'types.handlingUnits.rules.intro',
      itemKeys: [
        { key: 'types.handlingUnits.rules.existingOrder' },
        { key: 'types.handlingUnits.rules.multipleOrders' },
      ],
      titleKey: 'types.handlingUnits.rules.title',
    },
  ],
  templateEndpoint: '/import/templates/handling-units',
  templateNameKey: 'types.handlingUnits.templateName',
  type: IMPORT_TYPE.HANDLING_UNITS,
  uploadEndpoint: '/import/handling-units',
};

export const IMPORT_TYPE_CONFIGS: readonly ImportTypeConfig[] = [ORDERS_CONFIG, HANDLING_UNITS_CONFIG];
